import { ActiveMetricTabs } from '@/Enums/Attribution/ActiveMetricTabs.enum';
import ChartMetric from '@/Enums/Chart/Metric.enum';
import { BeeswaxReportProperties } from '@/Enums/Report/BeeswaxReportProperties.enum';
import { IPublisherArrayData } from '@/Interfaces/Attribution/Publisher.interface';
import {
  IBaseReportRecord,
  ICreativeReportRecord,
  IPublisherReportRecord,
} from '@/Interfaces/Attribution/ReportRecord.interface';
import { CHART_COLORS, PRIMARY } from '@/constants/color';
import { PUBLISHER_NAME } from '@/constants/reporting';
import { createObjWithUniqueFields } from '@/helpers/mapping';
import { ChartData } from 'chart.js';

export const getDoughnutGraphData = <TReportRecord = IBaseReportRecord>(
  activeTab: ActiveMetricTabs,
  reportType: string,
  reportData: TReportRecord[],
  loading: boolean,
) => {
  const chartData: ChartData<'doughnut'> = {
    labels: [],
    datasets: [
      {
        label: activeTab,
        data: [],
        backgroundColor: [],
        borderColor: 'white',
      },
    ],
  };

  if (!reportData || loading) return { chartData, csvData: {}, loading };

  const fieldValueName =
    activeTab === ActiveMetricTabs.Conversion
      ? BeeswaxReportProperties.IP_CONVERSIONS
      : BeeswaxReportProperties.IMPRESSIONS;

  const objOfData = createObjWithUniqueFields(reportData, reportType, fieldValueName) as Record<string, number>;

  for (const [key, val] of Object.entries(objOfData)) {
    chartData.labels!.push(key);
    (chartData.datasets[0].backgroundColor as string[]).push(CHART_COLORS[key]);
    chartData.datasets[0].data.push(val);
  }

  return { chartData, csvData: objOfData, loading };
};

const getChartUpdateData = (processedReportData: Record<string, number>) => {
  const labels: string[] = [];
  const data: number[] = [];
  for (const [key, val] of Object.entries(processedReportData)) {
    labels.push(key);
    data.push(val);
  }

  return { labels, data };
};

export const getHorizontalGraphData = <TReportRecord = IBaseReportRecord>(
  reportType: string,
  reportData: TReportRecord[],
  loading: boolean,
) => {
  const chartData: ChartData<'bar'> = {
    labels: [],
    datasets: [
      {
        label: 'Conversion',
        data: [],
        backgroundColor: [PRIMARY],
        borderColor: 'white',
      },
    ],
  };

  if (!reportData || loading) return { chartData, csvData: {} };

  const objOfData = createObjWithUniqueFields(
    reportData,
    reportType === 'Creative' ? 'Creative Name' : reportType,
    BeeswaxReportProperties.IP_CONVERSIONS,
  ) as Record<string, number>;

  if (reportType === 'Creative') {
    const labels: string[] = [];
    const data: number[] = [];
    (reportData as ICreativeReportRecord[]).forEach(item => {
      labels.push(item[BeeswaxReportProperties.CREATIVE_NAME]);
      data.push(item[BeeswaxReportProperties.IP_CONVERSIONS]);
    });
    chartData.labels = labels;
    chartData.datasets[0].data = data;
  } else {
    const { labels, data } = getChartUpdateData(objOfData);
    chartData.labels = labels;
    chartData.datasets[0].data = data;
  }

  return { chartData, csvData: objOfData };
};

export const getVerticalFootTrafficGraphData = <T>(reportData: T, loading: boolean) => {
  const chartData: ChartData<'bar'> = {
    labels: [],
    datasets: [{ label: ChartMetric.FootTrafficVisits, data: [], backgroundColor: [PRIMARY], borderColor: 'white' }],
  };

  if (!reportData || loading) return { chartData, csvData: {} };

  const { labels, data } = getChartUpdateData(reportData);
  chartData.labels = labels;
  chartData.datasets[0].data = data;

  return { chartData, csvData: reportData };
};

const createPublisherObject = (arrayOfElements: IPublisherReportRecord[]) => {
  const newObj: {
    [key: string]: IPublisherArrayData;
  } = {};

  arrayOfElements.forEach(report => {
    if (newObj[report[PUBLISHER_NAME]]) {
      newObj[report[PUBLISHER_NAME]] = {
        [BeeswaxReportProperties.IP_CONVERSIONS]:
          newObj[report[PUBLISHER_NAME]][BeeswaxReportProperties.IP_CONVERSIONS] +
          report[BeeswaxReportProperties.IP_CONVERSIONS],
        Logo: report.Logo,
      };
    } else {
      newObj[report[PUBLISHER_NAME]] = {
        [BeeswaxReportProperties.IP_CONVERSIONS]: report[BeeswaxReportProperties.IP_CONVERSIONS],
        Logo: report.Logo,
      };
    }
  });

  return newObj;
};

const getPublisherChartUpdateData = (processedReportData: {
  [key: string]: IPublisherArrayData;
}) => {
  const labels: string[] = [];
  const data: number[] = [];
  const logos: Array<string | null> = [];
  for (const [key, val] of Object.entries(processedReportData)) {
    labels.push(key);
    data.push(val[BeeswaxReportProperties.IP_CONVERSIONS]);
    logos.push(val?.Logo || null);
  }

  return { labels, data, logos };
};

export const getPublishersGraphData = (reportData: IPublisherReportRecord[], loading: boolean) => {
  const chartData: ChartData<'bar'> = {
    labels: [],
    datasets: [
      {
        label: 'Conversion',
        data: [],
        backgroundColor: [PRIMARY],
        borderColor: 'white',
      },
    ],
  };
  if (!reportData || loading) return { chartData, csvData: {}, loading, logos: [] as string[] };

  const objOfData = createPublisherObject(reportData);

  const { labels, data, logos } = getPublisherChartUpdateData(objOfData);

  chartData.labels = labels;
  chartData.datasets[0].data = data;
  return { chartData, csvData: objOfData, loading, logos: logos as string[] };
};

export const scaleFunnelValues = (data: number[]) => {
  const minValue = Math.min(...data);
  const maxValue = Math.max(...data);

  if (minValue === maxValue) {
    return data;
  }

  // Logarithmic scaling to preserve proportions between values
  return data.map(value => Math.log(value + 1) / Math.log(maxValue + 1));
};
